import { useState, useEffect, useRef } from "react";

const STORAGE_KEY_PREFIX = "589comps_list_display_";

// Shared pagination/infinite-scroll state and fetching logic for any admin
// list view (activity log, stats tables, etc). The root-configured
// sitewide defaults (mode/page size, from siteSettings.get) are only the
// STARTING point — once a viewer changes either for themselves on a given
// list, that choice is remembered per-browser (keyed by listKey, so
// different lists can have different remembered choices) rather than
// reverting to root's default on the next visit or being overridden if
// root later changes the default.
//
// fetchPage: ({offset, limit}) => Promise<{entries, totalMatching}> — the
// same shape buildActivityLogResponse (and any similar backend query
// function) already returns.
// deps: extra values (e.g. a search box's text, a filter dropdown) that
// should reset back to page 1 / a fresh load when they change, same as a
// mode or page-size change would.
export function usePaginatedList({ listKey, fetchPage, defaultMode, defaultPageSize, deps = [] }) {
  const storageKey = STORAGE_KEY_PREFIX + listKey;
  const readStored = () => {
    try {
      return JSON.parse(localStorage.getItem(storageKey));
    } catch {
      return null;
    }
  };

  const [mode, setModeState] = useState(() => readStored()?.mode || defaultMode || "pagination");
  const [pageSize, setPageSizeState] = useState(() => readStored()?.pageSize || defaultPageSize || 25);

  // Root's defaults arrive asynchronously (fetched from the server, not
  // known on first render) — adopt them once known, but only if this
  // viewer has never made their own choice for this specific list before,
  // so a fresh root default doesn't silently override a choice already
  // made for this list.
  const hasStoredChoiceRef = useRef(readStored() != null);
  useEffect(() => {
    if (hasStoredChoiceRef.current) return;
    if (defaultMode) setModeState(defaultMode);
    if (defaultPageSize) setPageSizeState(defaultPageSize);
  }, [defaultMode, defaultPageSize]);

  const persist = (next) => {
    hasStoredChoiceRef.current = true;
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Unavailable/full — the choice just won't persist across visits,
      // not worth surfacing an error for a display preference.
    }
  };
  const setMode = (next) => {
    setModeState(next);
    persist({ mode: next, pageSize });
  };
  const setPageSize = (next) => {
    setPageSizeState(next);
    persist({ mode, pageSize: next });
  };

  const [page, setPage] = useState(1);
  const [items, setItems] = useState(null); // null = not yet loaded
  const [totalMatching, setTotalMatching] = useState(0);
  const [loading, setLoading] = useState(false);
  // Guards against a slow, now-superseded request overwriting a later
  // one's result — e.g. typing quickly in a search box could otherwise
  // let an earlier keystroke's response land after a later one's.
  const requestIdRef = useRef(0);

  const fetchAndSet = (offset, append) => {
    const myId = ++requestIdRef.current;
    setLoading(true);
    fetchPage({ offset, limit: pageSize })
      .then((data) => {
        if (myId !== requestIdRef.current) return;
        setTotalMatching(data.totalMatching);
        setItems((prev) => (append && prev ? [...prev, ...data.entries] : data.entries));
      })
      .catch(() => {})
      .finally(() => {
        if (myId === requestIdRef.current) setLoading(false);
      });
  };

  // Distinguishes "a filter/mode/page-size actually changed" (reset to
  // page 1 and fetch that) from "the page number itself changed" (fetch
  // whatever page was requested) — both end up calling fetchAndSet, but
  // computing the right offset directly here (rather than relying on a
  // separate effect reacting to a setPage(1) call one render later) means
  // a filter change never briefly fetches with the previous, now-stale
  // page number before correcting itself.
  const filterKey = JSON.stringify([mode, pageSize, ...deps]);
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    const filtersChanged = prevFilterKeyRef.current !== filterKey;
    prevFilterKeyRef.current = filterKey;
    const effectivePage = filtersChanged ? 1 : page;
    if (filtersChanged && page !== 1) setPage(1);
    fetchAndSet(mode === "pagination" ? (effectivePage - 1) * pageSize : 0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page]);

  const loadMore = () => {
    if (loading) return;
    fetchAndSet(items?.length || 0, true);
  };

  const reload = () => fetchAndSet(mode === "pagination" ? (page - 1) * pageSize : 0, false);

  const totalPages = Math.max(1, Math.ceil(totalMatching / pageSize));
  const hasMore = (items?.length || 0) < totalMatching;

  // A PAGE THAT NO LONGER EXISTS.
  //
  // Staying put when the list reloads is right — somebody reading page 3
  // should not be thrown to the top because a row changed elsewhere. But
  // when the list SHRINKS past the current page, staying put means an
  // empty list beside a count saying there are ten of them.
  //
  // Found on 2 Sep 2026: eleven queued competitions, page size ten, one
  // moved to "To reschedule". Ten left is exactly one page, and page two showed
  // nothing while the tab label correctly read 10.
  //
  // Clamps rather than resetting to 1: falling back to the last page that
  // does exist is closer to where the reader was.
  useEffect(() => {
    if (mode !== "pagination") return;
    if (loading) return;
    if (page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages, mode, loading]);

  return {
    mode,
    setMode,
    pageSize,
    setPageSize,
    page,
    setPage,
    items,
    totalMatching,
    totalPages,
    loading,
    loadMore,
    hasMore,
    reload,
  };
}
